import React, { useState } from 'react';
import { Select, DatePicker, Slider, Form, Input } from 'antd';
import { ButtonCommon, SelectCommon } from 'components/common';
import { CaretDown, Calendar, Funnel, XCircle } from '@phosphor-icons/react';

import { formatPrice } from 'utils';
import { Customer, Supplier } from 'models';

import { DebtFilter, DebtStatusCode } from 'models/Debt';
import { QueryParams } from 'types';
import { useGetCustomersQuery, useGetSuppliersQuery } from 'services';

interface FilterDebtProps {
  initialFilter?: DebtFilter;
  onApplyFilter: (filter: DebtFilter) => void;
  onClearFilter: () => void;
  type?: 'CUSTOMER' | 'SUPPLIER';
  isDashboard?: boolean;
}

const FilterDebt: React.FC<FilterDebtProps> = ({
  initialFilter,
  onApplyFilter,
  onClearFilter,
  type = 'CUSTOMER',
  isDashboard = false,
}) => {
  const [form] = Form.useForm();
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 150000000]);
  const supplierQueryParams: QueryParams = {
    size: 100,
    page: 1,
    sort: 'createdAt:asc',
    include: 'baseUser',
  } as unknown as QueryParams;
  const { data: suppliersData } = useGetSuppliersQuery({
    query: supplierQueryParams,
  });
  const customerQueryParams: QueryParams = {
    size: 100,
    page: 1,
    sort: 'createdAt:asc',
    include: 'baseUser',
  } as unknown as QueryParams;
  const { data: customersData } = useGetCustomersQuery({
    query: customerQueryParams,
  });
  const suppliers: Supplier[] = suppliersData?.data?.rows || [];
  const customers: Customer[] = customersData?.data?.rows || [];
  const handleClearFilter = () => {
    form.resetFields();
    setPriceRange(priceRange);
    form.setFieldsValue({
      createdAt: null,
      debtType: type,
      phoneNumber: undefined,
      refTable: undefined,
      code: undefined,
      status: initialFilter?.status,
      codeSearch: undefined,
      deadline: initialFilter?.deadline,
    });
    onClearFilter();
  };

  const handleApplyFilter = () => {
    const values = form.getFieldsValue();
    onApplyFilter({
      ...values,
      priceRange,
      deadline: initialFilter?.deadline,
    });
  };
  React.useEffect(() => {
    if (!isDashboard) {
      handleClearFilter();
    } else {
      form.setFieldsValue({
        debtType: type,
        status: initialFilter?.status,
        deadline: initialFilter?.deadline,
      });
    }
  }, [type, form]);

  return (
    <div className='bg-white rounded-lg overflow-hidden w-full '>
      <div className='flex justify-between items-center px-6 py-3'>
        <h2 className='text-xl font-bold text-[var(--textBlack100)]'>Bộ lọc</h2>
      </div>

      <div className='px-6 py-3'>
        <Form form={form} layout='vertical' className='filter-form'>
          <div className='flex flex-col'>
            {!isDashboard && (
              <Form.Item
                label={
                  <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                    Loại công nợ
                  </span>
                }
                name='debtType'
              >
                <Select
                  placeholder='Chọn loại công nợ'
                  className={'w-full' + (!isDashboard ? ' pointer-events-none cursor-not-allowed' : '')}
                  allowClear={{
                    clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                  }}
                  suffixIcon={<CaretDown color='black' size={18} />}
                  options={[
                    { value: 'CUSTOMER', label: 'Công nợ khách hàng' },
                    { value: 'SUPPLIER', label: 'Công nợ nhà cung cấp' },
                  ]}
                />
              </Form.Item>
            )}
            {type === 'CUSTOMER' && (
              <Form.Item
                label={
                  <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Khách hàng</span>
                }
                name='phoneNumber'
              >
                <SelectCommon
                  placeholder='Chọn khách hàng'
                  className='w-full'
                  allowClear={{
                    clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                  }}
                  sizeSelect='sm'
                  selectType='filterSelect'
                  suffixIcon={<CaretDown color='black' size={18} />}
                  options={customers?.map((customer: Customer) => ({
                    value: customer.baseUser?.phoneNumber || '',
                    label: customer.baseUser?.name,
                  }))}
                />
              </Form.Item>
            )}
            {type === 'SUPPLIER' && (
              <Form.Item
                label={
                  <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                    Nhà cung cấp
                  </span>
                }
                name='phoneNumber'
              >
                <SelectCommon
                  placeholder='Chọn nhà cung cấp'
                  className='w-full'
                  allowClear={{
                    clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                  }}
                  sizeSelect='sm'
                  selectType='filterSelect'
                  suffixIcon={<CaretDown color='black' size={18} />}
                  options={suppliers?.map((supplier: Supplier) => ({
                    value: supplier.baseUser.phoneNumber || '',
                    label: supplier?.baseUser?.name,
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Thời gian</span>
              }
              name='createdAt'
            >
              <DatePicker
                className='w-full'
                placeholder='Ngày - tháng - năm'
                format='DD/MM/YYYY'
                suffixIcon={<Calendar className='text-[var(--textBlack100)]' />}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Mã phiếu </span>
              }
              name='codeSearch'
            >
              <Input
                placeholder='Nhập mã phiếu xuất kho'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                className='w-full'
              />
            </Form.Item>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Loại giao dịch
                </span>
              }
              name='refTable'
            >
              <Select
                placeholder='Chọn loại phiếu'
                className='w-full'
                defaultValue=''
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                options={[
                  { value: '', label: 'Tất cả ' },
                  { value: 'InventoryDocuments', label: 'Nhập hàng' },
                  { value: 'SaleOrders', label: 'Bán hàng' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Trạng thái phiếu
                </span>
              }
              name='status'
            >
              <Select
                placeholder='Chọn trạng thái'
                className='w-full'
                defaultValue={''}
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                options={[
                  { value: '', label: 'Tất cả trạng thái' },
                  { value: DebtStatusCode.WAITING, label: 'Đợi thu' },
                  { value: DebtStatusCode.REFUND, label: 'Phải trả' },
                  { value: DebtStatusCode.CONFIRMING, label: 'Đợi xác nhận' },
                  { value: DebtStatusCode.COMPLETED, label: 'Hoàn tất' },
                  // { value: DebtStatusCode.OVERDUE, label: 'Trễ hẹn' },
                ]}
              />
            </Form.Item>

            <div className='mb-[24px]'>
              <p className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)] mb-2'>Khoảng giá</p>
              <Slider
                range
                min={0}
                max={150000000}
                value={priceRange}
                onChange={(value) => setPriceRange(value as [number, number])}
                className=' my-2'
                trackStyle={[{ backgroundColor: 'var(--primaryColor500)' }]}
                handleStyle={[
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                ]}
                railStyle={{ backgroundColor: 'var(--primaryColor50)' }}
              />
              <div className='flex justify-between'>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[0])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[1])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
              </div>
            </div>
          </div>
        </Form>

        <div className='flex gap-6'>
          <ButtonCommon
            color='none'
            size='sm'
            radius='rounded'
            className='min-w-[112px] hover:font-normal'
            onClick={handleClearFilter}
          >
            Xóa bộ lọc
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='sm'
            radius='rounded'
            fullWidth
            className='flex justify-center items-center'
            onClick={handleApplyFilter}
            icon={<Funnel size={16} />}
            iconPosition='right'
          >
            Áp dụng bộ lọc
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default FilterDebt;
